import { Tag } from "@narmi/design_system";
import cc from "classcat";
import { ReactNode } from "react";
import styles from "./StatusTimeline.module.css";

interface TimelineStepProps {
  title: string;
  description: string;
  estimatedTime?: string;
  isComplete: boolean;
  isPrimary?: boolean;
  showLine?: boolean;
  stepNumber: number;
  children?: ReactNode;
}

function TimelineStep({
  title,
  description,
  estimatedTime,
  isComplete,
  isPrimary = false,
  showLine = false,
  stepNumber,
  children,
}: TimelineStepProps) {
  const mobileIcon = (
    <div
      className={cc([
        styles.timelineIconMobile,
        isComplete ? styles.timelineIconComplete : styles.timelineIconPending,
      ])}
    >
      {isComplete ? (
        <i className={cc(["narmi-icon-check", styles.checkIconMobile])} />
      ) : (
        <span className="fontWeight--bold">{stepNumber}</span>
      )}
    </div>
  );

  const desktopIcon = (
    <div
      className={cc([
        styles.timelineIcon,
        isComplete ? styles.timelineIconComplete : styles.timelineIconPending,
      ])}
    >
      {isComplete ? (
        <i className={cc(["narmi-icon-check", styles.checkIcon])} />
      ) : (
        <span className="fontWeight--bold">{stepNumber}</span>
      )}
    </div>
  );

  const cardContent = (
    <div className={cc([styles.card, isPrimary ? styles.cardPrimary : styles.cardSecondary])}>
      <div className={styles.cardContent}>
        <div className={styles.cardHeader}>
          <div className={styles.cardHeaderLeft}>
            <div className={cc([styles.cardTitle, !isPrimary && styles.cardTitleSecondary])}>
              {title}
            </div>
            <div className={styles.cardDescription}>{description}</div>
          </div>
          {estimatedTime && <Tag label={estimatedTime} kind="subdued" />}
        </div>
        {children}
      </div>
    </div>
  );

  return (
    <>
      {/* Mobile layout: icon overlaps card corner, line is separate element */}
      <div className={styles.timelineItemMobile}>
        <div className={styles.timelineIndicatorMobile}>{mobileIcon}</div>
        {showLine && (
          <div
            className={cc([
              styles.timelineLineMobile,
              isPrimary && styles.timelineLineMobilePrimary,
            ])}
          />
        )}
        <div>{cardContent}</div>
      </div>

      {/* Desktop layout: icon in separate column */}
      <div className={styles.timelineItemDesktop}>
        <div className={styles.timelineIndicator}>
          {desktopIcon}
          {showLine && (
            <div className={cc([styles.timelineLine, isPrimary && styles.timelineLinePrimary])} />
          )}
        </div>
        <div className={styles.timelineCard}>{cardContent}</div>
      </div>
    </>
  );
}

export { TimelineStep };
